import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { toast } from 'sonner'
import Canvas from '@/components/seating_planner/Canvas'
import EventDetails from '@/components/seating_planner/EventDetails'
import GuestPanel from '@/components/seating_planner/GuestPanel'
import ShareDialog from '@/components/seating_planner/ShareDialog'
import Toolbar from '@/components/seating_planner/Toolbar'
import { exportPlanToCsv } from '@/lib/seatingPlannerCsv'
import { exportPlanToPdf } from '@/lib/seatingPlannerPdf'
import { clearLocalPlan, loadLocalPlan, normalizeAssignments, saveLocalPlan } from '@/lib/seatingPlannerStorage'
import {
  DEFAULT_EVENT_DETAILS,
  DEFAULT_PLAN_STATE,
  type EventDetails as EventDetailsType,
  type PlanState,
  type SeatingGuest,
  type SeatingTable,
  type TableShape,
} from '@/types/seatingPlanner'
import type { SharedProps } from '@/types'

type PageProps = {
  token: string | null
  initial_state: PlanState | null
}

const TABLE_DEFAULTS = {
  round: { width: 140, height: 140, capacity: 10, rotation: 0 },
  rectangle: { width: 200, height: 100, capacity: 8, rotation: 0 },
} as const

const SAVE_DEBOUNCE_MS = 600
const REMOTE_SAVE_DEBOUNCE_MS = 1500

export default function SeatingPlannerPage({ token: initialToken, initial_state }: PageProps) {
  const { csrf_token: csrfToken } = usePage<SharedProps>().props
  const [token, setToken] = useState<string | null>(initialToken)
  const [state, setState] = useState<PlanState>(() => hydrateInitial(initial_state))
  const [activeDragType, setActiveDragType] = useState<'table' | 'guest' | null>(null)
  const [activeDropTableId, setActiveDropTableId] = useState<string | null>(null)
  const [activeDropSeat, setActiveDropSeat] = useState<{ tableId: string; seatIndex: number } | null>(null)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const scaleRef = useRef(1)
  const remoteSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialHydrated = useRef(false)

  const handleScaleChange = useCallback((next: number) => {
    scaleRef.current = next
  }, [])

  // Hydrate from localStorage on first render (only when no shared token).
  useEffect(() => {
    if (initialHydrated.current) return
    initialHydrated.current = true
    if (token) return
    const local = loadLocalPlan()
    if (local) setState(hydrateInitial(local))
  }, [token])

  // Persist locally on every change (debounced via React batching of setState).
  useEffect(() => {
    if (!initialHydrated.current) return
    if (token) return // server is the source of truth for shared plans
    const t = setTimeout(() => saveLocalPlan(state), SAVE_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [state, token])

  // Auto-PATCH for shared plans.
  useEffect(() => {
    if (!token || !initialHydrated.current) return
    if (remoteSaveTimer.current) clearTimeout(remoteSaveTimer.current)
    remoteSaveTimer.current = setTimeout(() => {
      sendUpdate(token, state, csrfToken).catch(() => {
        toast.error('Could not save changes — check your connection.')
      })
    }, REMOTE_SAVE_DEBOUNCE_MS)
    return () => {
      if (remoteSaveTimer.current) clearTimeout(remoteSaveTimer.current)
    }
  }, [state, token, csrfToken])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const activeType = args.active.data.current?.type

    if (activeType !== 'guest') return rectIntersection(args)

    const filterByType = (type: 'seat' | 'table', collisions: ReturnType<CollisionDetection>) =>
      collisions.filter((collision) => collision.data?.droppableContainer.data.current?.type === type)

    const pointerCollisions = pointerWithin(args)
    const pointerSeats = filterByType('seat', pointerCollisions)
    if (pointerSeats.length > 0) return pointerSeats

    const pointerTables = filterByType('table', pointerCollisions)
    if (pointerTables.length > 0) return pointerTables

    return rectIntersection(args)
  }, [])

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { type?: 'table' | 'guest' } | undefined
    setActiveDragType(data?.type ?? null)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const overData = event.over?.data.current as
      | { type?: string; tableId?: string; seatIndex?: number }
      | undefined
    if (overData?.type === 'seat' && overData.tableId && typeof overData.seatIndex === 'number') {
      setActiveDropSeat({ tableId: overData.tableId, seatIndex: overData.seatIndex })
      setActiveDropTableId(null)
    } else if (overData?.type === 'table' && overData.tableId) {
      setActiveDropTableId(overData.tableId)
      setActiveDropSeat(null)
    } else {
      setActiveDropTableId(null)
      setActiveDropSeat(null)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragType(null)
    setActiveDropTableId(null)
    setActiveDropSeat(null)
    const activeData = event.active.data.current as
      | { type?: 'table' | 'guest'; tableId?: string; guestId?: string }
      | undefined
    const overData = event.over?.data.current as
      | { type?: string; tableId?: string; seatIndex?: number }
      | undefined

    if (activeData?.type === 'table' && activeData.tableId) {
      const scale = scaleRef.current || 1
      const dx = event.delta.x / scale
      const dy = event.delta.y / scale
      if (dx === 0 && dy === 0) return
      setState((prev) => ({
        ...prev,
        tables: prev.tables.map((t) =>
          t.id === activeData.tableId
            ? { ...t, x: clamp(t.x + dx, 0, 4000), y: clamp(t.y + dy, 0, 4000) }
            : t,
        ),
      }))
      return
    }

    if (activeData?.type !== 'guest' || !activeData.guestId) return

    if (overData?.type === 'seat' && overData.tableId && typeof overData.seatIndex === 'number') {
      assignGuestToSeat(activeData.guestId, overData.tableId, overData.seatIndex)
      return
    }

    if (overData?.type === 'table' && overData.tableId) {
      assignGuestToTable(activeData.guestId, overData.tableId)
    }
  }

  const addTable = (shape: TableShape) => {
    if (state.tableLimit > 0 && state.tables.length >= state.tableLimit) {
      toast.error(`You've reached the table limit (${state.tableLimit}). Increase the limit to add more.`)
      return
    }
    const defaults = TABLE_DEFAULTS[shape]
    const number = String(state.tables.length + 1)
    setState((prev) => ({
      ...prev,
      tables: [
        ...prev.tables,
        {
          id: cryptoRandomId(),
          number,
          label: '',
          shape,
          capacity: defaults.capacity,
          x: 80 + ((prev.tables.length * 30) % 600),
          y: 80 + ((prev.tables.length * 30) % 400),
          width: defaults.width,
          height: defaults.height,
          rotation: defaults.rotation,
        },
      ],
    }))
  }

  const updateEvent = useCallback((patch: Partial<EventDetailsType>) => {
    setState((prev) => ({ ...prev, event: { ...prev.event, ...patch } }))
  }, [])

  const updateTableLimit = useCallback((value: number) => {
    setState((prev) => ({ ...prev, tableLimit: Math.max(0, Math.min(500, value)) }))
  }, [])

  const updateTable = useCallback((id: string, patch: Partial<SeatingTable>) => {
    setState((prev) => {
      const table = prev.tables.find((t) => t.id === id)
      if (!table) return prev

      const nextTables = prev.tables.map((t) => (t.id === id ? { ...t, ...patch } : t))
      let nextAssignments = prev.assignments

      if (typeof patch.capacity === 'number' && patch.capacity > 0 && patch.capacity !== table.capacity) {
        const normalized = reconcileTableAssignments(prev.assignments, id, patch.capacity)
        nextAssignments = normalized.assignments

        if (normalized.unassignedGuestIds.length > 0) {
          toast.error(
            `${normalized.unassignedGuestIds.length} guest${
              normalized.unassignedGuestIds.length === 1 ? ' was' : 's were'
            } unassigned because Table ${table.number || '?'} now has fewer seats.`,
          )
        }
      }

      return {
        ...prev,
        tables: nextTables,
        assignments: nextAssignments,
      }
    })
  }, [])

  const deleteTable = useCallback((id: string) => {
    setState((prev) => {
      const nextAssignments: typeof prev.assignments = {}
      for (const [guestId, assignment] of Object.entries(prev.assignments)) {
        if (assignment.tableId !== id) nextAssignments[guestId] = assignment
      }
      return {
        ...prev,
        tables: prev.tables.filter((t) => t.id !== id),
        assignments: nextAssignments,
      }
    })
  }, [])

  const addGuest = useCallback((guest: SeatingGuest) => {
    setState((prev) => ({ ...prev, guests: [...prev.guests, guest] }))
  }, [])

  const addGuests = useCallback((guests: SeatingGuest[]) => {
    setState((prev) => ({ ...prev, guests: [...prev.guests, ...guests] }))
  }, [])

  const updateGuest = useCallback((id: string, patch: Partial<SeatingGuest>) => {
    setState((prev) => ({
      ...prev,
      guests: prev.guests.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    }))
  }, [])

  const deleteGuest = useCallback((id: string) => {
    setState((prev) => {
      const { [id]: _removed, ...rest } = prev.assignments
      return {
        ...prev,
        guests: prev.guests.filter((g) => g.id !== id),
        assignments: rest,
      }
    })
  }, [])

  const unassignGuest = useCallback((id: string) => {
    setState((prev) => {
      const { [id]: _removed, ...rest } = prev.assignments
      return { ...prev, assignments: rest }
    })
  }, [])

  const assignGuestToSeat = useCallback((guestId: string, tableId: string, seatIndex: number) => {
    setState((prev) => {
      const currentAssignment = prev.assignments[guestId]
      if (currentAssignment?.tableId === tableId && currentAssignment.seatIndex === seatIndex) return prev

      let displacedGuestId: string | null = null
      for (const [gid, assignment] of Object.entries(prev.assignments)) {
        if (gid !== guestId && assignment.tableId === tableId && assignment.seatIndex === seatIndex) {
          displacedGuestId = gid
          break
        }
      }

      if (!displacedGuestId) {
        return {
          ...prev,
          assignments: {
            ...prev.assignments,
            [guestId]: { tableId, seatIndex },
          },
        }
      }

      if (!currentAssignment) {
        toast.error('That seat is occupied. Drop onto an empty seat or the table itself.')
        return prev
      }

      return {
        ...prev,
        assignments: {
          ...prev.assignments,
          [guestId]: { tableId, seatIndex },
          [displacedGuestId]: currentAssignment,
        },
      }
    })
  }, [])

  const assignGuestToTable = useCallback((guestId: string, tableId: string) => {
    setState((prev) => {
      const table = prev.tables.find((t) => t.id === tableId)
      if (!table) return prev
      const currentAssignment = prev.assignments[guestId]
      if (currentAssignment?.tableId === tableId) return prev

      const used = new Set<number>()
      for (const [gid, a] of Object.entries(prev.assignments)) {
        if (gid !== guestId && a.tableId === tableId) used.add(a.seatIndex)
      }
      let seatIndex = -1
      for (let i = 0; i < table.capacity; i++) {
        if (!used.has(i)) {
          seatIndex = i
          break
        }
      }
      if (seatIndex === -1) {
        toast.error(`Table ${table.number || '?'} is full.`)
        return prev
      }
      return {
        ...prev,
        assignments: { ...prev.assignments, [guestId]: { tableId, seatIndex } },
      }
    })
  }, [])

  const handleResetAll = () => {
    setState(DEFAULT_PLAN_STATE)
    clearLocalPlan()
    if (token) {
      router.visit('/seating-planner')
    }
  }

  const handleDownloadCsv = () => {
    if (state.guests.length === 0) {
      toast.error('Add some guests before downloading the CSV.')
      return
    }
    try {
      exportPlanToCsv(state)
      toast.success('CSV downloaded.')
    } catch (err) {
      console.error(err)
      toast.error('Could not generate the CSV.')
    }
  }

  const handleDownloadPdf = async () => {
    if (!canvasRef.current) return
    setExporting(true)
    try {
      await exportPlanToPdf(state, canvasRef.current)
      toast.success('PDF downloaded.')
    } catch (err) {
      console.error(err)
      toast.error('Could not generate the PDF. Try again in a moment.')
    } finally {
      setExporting(false)
    }
  }

  const handleSaveAndShare = async () => {
    setSaving(true)
    try {
      if (token) {
        const result = await sendUpdate(token, state, csrfToken)
        setShareUrl(result.url)
      } else {
        const result = await sendCreate(state, csrfToken)
        setToken(result.token)
        setShareUrl(result.url)
        clearLocalPlan()
        // Replace the URL so reload loads from server instead of localStorage.
        window.history.replaceState({}, '', `/seating-planner/${result.token}`)
      }
      setShareOpen(true)
    } catch (err) {
      console.error(err)
      toast.error('Could not save the plan. Try again in a moment.')
    } finally {
      setSaving(false)
    }
  }

  const guestsCount = state.guests.length
  const seatedCount = useMemo(
    () => state.guests.filter((g) => state.assignments[g.id]).length,
    [state.guests, state.assignments],
  )

  return (
    <>
      <Head title="Seating Planner">
        <meta
          head-key="description"
          name="description"
          content="Free interactive seating-chart planner. Add tables, drag-and-drop guests, and download a printable PDF."
        />
      </Head>

      <div className="bg-linear-to-br from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <h1 className="max-w-[24ch] text-3xl font-semibold tracking-tight text-white text-balance sm:text-4xl">
            Seating Planner
          </h1>
          <p className="mt-3 max-w-[60ch] text-base font-medium text-white/85 text-pretty">
            Sketch your gala&rsquo;s seating chart in minutes. Add round and rectangle tables, drop guests where they belong, then download a printable PDF.
          </p>
          <div className="mt-3 inline-flex items-center gap-3 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90 ring-1 ring-white/20">
            <span>{state.tables.length} tables</span>
            <span className="opacity-50">·</span>
            <span>{seatedCount}/{guestsCount} guests seated</span>
            {token && (
              <>
                <span className="opacity-50">·</span>
                <span>shared link saved</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1800px] flex-col gap-3 px-4 py-4 sm:px-6 lg:px-8">
        <DndContext
          collisionDetection={collisionDetection}
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <EventDetails event={state.event} onChange={updateEvent} />

          <Toolbar
            detailedView={state.view.detailedTables}
            saving={saving}
            exporting={exporting}
            tableCount={state.tables.length}
            tableLimit={state.tableLimit}
            onAddRoundTable={() => addTable('round')}
            onAddRectangleTable={() => addTable('rectangle')}
            onToggleDetailedView={() =>
              setState((prev) => ({ ...prev, view: { ...prev.view, detailedTables: !prev.view.detailedTables } }))
            }
            onChangeTableLimit={updateTableLimit}
            onResetAll={handleResetAll}
            onDownloadPdf={handleDownloadPdf}
            onDownloadCsv={handleDownloadCsv}
            onSaveAndShare={handleSaveAndShare}
          />

          <div className="flex h-[calc(100vh-22rem)] min-h-[600px] gap-3 overflow-hidden">
            <GuestPanel
              guests={state.guests}
              tables={state.tables}
              assignments={state.assignments}
              onAddGuest={addGuest}
              onAddGuests={addGuests}
              onUpdateGuest={updateGuest}
              onDeleteGuest={deleteGuest}
              onUnassignGuest={unassignGuest}
              onAssignGuestToTable={assignGuestToTable}
            />
            <div className="min-h-0 min-w-0 flex-1 overflow-auto rounded-xl bg-muted/30 p-3 ring-1 ring-black/5">
              <Canvas
                ref={canvasRef}
                state={state}
                activeDropTableId={activeDragType === 'guest' ? activeDropTableId : null}
                activeDropSeat={activeDragType === 'guest' ? activeDropSeat : null}
                onUpdateTable={updateTable}
                onDeleteTable={deleteTable}
                onUnassignGuest={unassignGuest}
                onScaleChange={handleScaleChange}
              />
            </div>
          </div>
        </DndContext>
      </div>

      <ShareDialog open={shareOpen} url={shareUrl} onClose={() => setShareOpen(false)} />
    </>
  )
}

function hydrateInitial(payload: PlanState | null): PlanState {
  if (!payload || typeof payload !== 'object') return DEFAULT_PLAN_STATE
  const tables = Array.isArray(payload.tables) ? payload.tables : []
  return {
    ...DEFAULT_PLAN_STATE,
    ...payload,
    view: { ...DEFAULT_PLAN_STATE.view, ...(payload.view || {}) },
    tables,
    guests: Array.isArray(payload.guests) ? payload.guests : [],
    assignments: reconcileAssignmentsWithTables(
      normalizeAssignments(payload.assignments, Array.isArray(payload.guests) ? payload.guests : []),
      tables,
    ).assignments,
    event: { ...DEFAULT_EVENT_DETAILS, ...(payload.event || {}) },
    tableLimit: typeof payload.tableLimit === 'number' && payload.tableLimit >= 0 ? payload.tableLimit : 0,
  }
}

function reconcileAssignmentsWithTables(
  assignments: Record<string, { tableId: string; seatIndex: number }>,
  tables: SeatingTable[],
): { assignments: PlanState['assignments']; unassignedGuestIds: string[] } {
  const next: PlanState['assignments'] = {}
  const unassignedGuestIds: string[] = []
  const entriesByTable = new Map<string, Array<[string, PlanState['assignments'][string]]>>()
  const validTableIds = new Set(tables.map((table) => table.id))

  for (const [guestId, assignment] of Object.entries(assignments)) {
    if (!validTableIds.has(assignment.tableId)) {
      unassignedGuestIds.push(guestId)
      continue
    }
    const entries = entriesByTable.get(assignment.tableId) || []
    entries.push([guestId, assignment])
    entriesByTable.set(assignment.tableId, entries)
  }

  for (const table of tables) {
    const normalized = reconcileSeatEntries(entriesByTable.get(table.id) || [], table.id, table.capacity)
    Object.assign(next, normalized.assignments)
    unassignedGuestIds.push(...normalized.unassignedGuestIds)
  }

  return { assignments: next, unassignedGuestIds }
}

function reconcileTableAssignments(
  assignments: PlanState['assignments'],
  tableId: string,
  capacity: number,
): { assignments: PlanState['assignments']; unassignedGuestIds: string[] } {
  const next: PlanState['assignments'] = {}
  const tableEntries: Array<[string, PlanState['assignments'][string]]> = []

  for (const [guestId, assignment] of Object.entries(assignments)) {
    if (assignment.tableId === tableId) {
      tableEntries.push([guestId, assignment])
    } else {
      next[guestId] = assignment
    }
  }

  const normalized = reconcileSeatEntries(tableEntries, tableId, capacity)
  return {
    assignments: { ...next, ...normalized.assignments },
    unassignedGuestIds: normalized.unassignedGuestIds,
  }
}

function reconcileSeatEntries(
  entries: Array<[string, PlanState['assignments'][string]]>,
  tableId: string,
  capacity: number,
): { assignments: PlanState['assignments']; unassignedGuestIds: string[] } {
  const next: PlanState['assignments'] = {}
  const usedSeats = new Set<number>()
  const pendingGuestIds: string[] = []

  const sortedEntries = [...entries].sort(([, a], [, b]) => a.seatIndex - b.seatIndex)

  for (const [guestId, assignment] of sortedEntries) {
    const seatIndex = assignment.seatIndex
    if (seatIndex >= 0 && seatIndex < capacity && !usedSeats.has(seatIndex)) {
      next[guestId] = { tableId, seatIndex }
      usedSeats.add(seatIndex)
    } else {
      pendingGuestIds.push(guestId)
    }
  }

  for (let seatIndex = 0; seatIndex < capacity && pendingGuestIds.length > 0; seatIndex += 1) {
    if (usedSeats.has(seatIndex)) continue
    const guestId = pendingGuestIds.shift()
    if (!guestId) break
    next[guestId] = { tableId, seatIndex }
    usedSeats.add(seatIndex)
  }

  return { assignments: next, unassignedGuestIds: pendingGuestIds }
}

async function sendCreate(state: PlanState, csrfToken: string): Promise<{ token: string; url: string }> {
  const res = await fetch('/seating-planner', {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ payload: state }),
  })
  if (!res.ok) throw new Error(`save_failed:${res.status}`)
  return (await res.json()) as { token: string; url: string }
}

async function sendUpdate(
  token: string,
  state: PlanState,
  csrfToken: string,
): Promise<{ token: string; url: string }> {
  const res = await fetch(`/seating-planner/${token}`, {
    method: 'PATCH',
    headers: {
      'X-CSRF-Token': csrfToken,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ payload: state }),
  })
  if (!res.ok) throw new Error(`update_failed:${res.status}`)
  return (await res.json()) as { token: string; url: string }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `id_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`
}
